import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { FileImage, Loader2, PenLine, Sparkles, Upload, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCertificateSettings,
  useUpdateCertificateSettings,
  type UpdateCertificateSettingsDTO,
} from '@/services/super-admin/certificate-settings.service'
import {
  useUploadCertificateBackground,
  useUploadCertificateSignature,
} from '@/services/upload.service'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'

/**
 * Tela "Certificado Global" pro super_admin (singleton).
 *
 * Define o template/assinatura/nome do diretor da FCamara que serão
 * usados como FALLBACK quando uma empresa não cadastrou seu próprio.
 * Também é o que aparece como assinatura secundária quando a empresa
 * marca `includeGlobalSignature: true`.
 */
export function CertificateSettingsManagement() {
  const { data, isLoading, isError } = useCertificateSettings()
  const update = useUpdateCertificateSettings()
  const uploadBg = useUploadCertificateBackground()
  const uploadSig = useUploadCertificateSignature()
  const { toast } = useToast()

  const bgInputRef = useRef<HTMLInputElement>(null)
  const sigInputRef = useRef<HTMLInputElement>(null)

  const [draft, setDraft] = useState<UpdateCertificateSettingsDTO>({})

  useEffect(() => {
    if (data && Object.keys(draft).length === 0) {
      setDraft({
        globalBackgroundUrl: data.globalBackgroundUrl,
        globalSignatureUrl: data.globalSignatureUrl,
        globalDirectorName: data.globalDirectorName,
        globalDirectorRole: data.globalDirectorRole,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  function patch<K extends keyof UpdateCertificateSettingsDTO>(
    key: K,
    value: UpdateCertificateSettingsDTO[K]
  ) {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  async function handleBgChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      // Sem companyId → o PHP usa 'global' no nome do arquivo
      const url = await uploadBg.mutateAsync({ file })
      patch('globalBackgroundUrl', url)
      toast({ title: 'Fundo global enviado' })
    } catch (err: unknown) {
      toast({ variant: 'destructive', title: 'Erro', description: getErrorMessage(err) })
    } finally {
      if (bgInputRef.current) bgInputRef.current.value = ''
    }
  }

  async function handleSigChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const url = await uploadSig.mutateAsync({ file })
      patch('globalSignatureUrl', url)
      toast({ title: 'Assinatura global enviada' })
    } catch (err: unknown) {
      toast({ variant: 'destructive', title: 'Erro', description: getErrorMessage(err) })
    } finally {
      if (sigInputRef.current) sigInputRef.current.value = ''
    }
  }

  async function handleSave() {
    try {
      await update.mutateAsync(draft)
      toast({ title: 'Certificado global atualizado' })
    } catch (err: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar',
        description: getErrorMessage(err),
      })
    }
  }

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />
  }

  if (isError) {
    return (
      <Card className="glass-card border-border/50">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Não foi possível carregar a configuração global.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Certificado global (FCamara)
          </CardTitle>
          <CardDescription>
            Template padrão usado quando uma empresa não cadastrou seu próprio. Também aparece como
            assinatura secundária quando a empresa marca "Incluir assinatura global FCamara".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Fundo global (PNG/JPG/WebP, máx 5MB)</Label>
            <p className="text-xs text-muted-foreground">
              Redimensionado pra 1240×877 (A4 paisagem). Aplicado a todos os certificados de
              empresas sem template próprio.
            </p>
            <div className="flex items-center gap-4">
              {draft.globalBackgroundUrl ? (
                <img
                  src={draft.globalBackgroundUrl}
                  alt=""
                  className="w-40 h-28 rounded object-cover border border-border/40"
                />
              ) : (
                <div className="w-40 h-28 rounded bg-muted/40 border border-dashed border-border flex items-center justify-center">
                  <FileImage className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={bgInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleBgChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => bgInputRef.current?.click()}
                  disabled={uploadBg.isPending}
                  className="gap-1.5"
                >
                  {uploadBg.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {draft.globalBackgroundUrl ? 'Trocar' : 'Enviar'}
                </Button>
                {draft.globalBackgroundUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => patch('globalBackgroundUrl', null)}
                    className="gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="globalDirectorName">Nome do diretor FCamara</Label>
            <Input
              id="globalDirectorName"
              value={draft.globalDirectorName ?? ''}
              onChange={(e) => patch('globalDirectorName', e.target.value || null)}
              placeholder="Ex.: Lucas Diretor FCamara"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="globalDirectorRole">Cargo do diretor FCamara</Label>
            <Input
              id="globalDirectorRole"
              value={draft.globalDirectorRole ?? ''}
              onChange={(e) => patch('globalDirectorRole', e.target.value || null)}
              placeholder="Ex.: CEO"
            />
            <p className="text-xs text-muted-foreground">
              Aparece embaixo do nome na assinatura do certificado.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Assinatura do diretor FCamara</Label>
            <p className="text-xs text-muted-foreground">
              Redimensionada pra 600×200. PNG com fundo transparente é o recomendado.
            </p>
            <div className="flex items-center gap-4">
              {draft.globalSignatureUrl ? (
                <img
                  src={draft.globalSignatureUrl}
                  alt=""
                  className="w-32 h-12 rounded object-contain border border-border/40 bg-white/5"
                />
              ) : (
                <div className="w-32 h-12 rounded bg-muted/40 border border-dashed border-border flex items-center justify-center">
                  <PenLine className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={sigInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleSigChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => sigInputRef.current?.click()}
                  disabled={uploadSig.isPending}
                  className="gap-1.5"
                >
                  {uploadSig.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {draft.globalSignatureUrl ? 'Trocar' : 'Enviar'}
                </Button>
                {draft.globalSignatureUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => patch('globalSignatureUrl', null)}
                    className="gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={update.isPending} className="gap-2">
          {update.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Salvar configuração global
        </Button>
      </div>
    </div>
  )
}
